<template>
	<div
		@click="toggleLocked"
		:class="[isLockedClassname, unlockableClassname, {'tooltip tooltip--centered dark': disabled}]"
		class="field-locked"
		aria-label="field-locked"
	>
		<i class="fas fa-lock-open"></i>
		<i class="fas fa-lock"></i>
	</div>
</template>

<script>
import {computed, nextTick, onMounted, ref, watch} from 'vue';

export default {
	props: {
		field1: {
			type: [String, Number, Object],
		},
		field2: {
			type: [String, Number, Object],
		},
		maxLimit: {
			type: [Number, Object],
			default: null
		},
		locked: {
			type: Boolean,
			default: false
		},
		unlockable: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: true
		},
		ignoreWatch: {
			type: Boolean,
			default: false
		}
	},
	setup(props, {emit}) {
		const isLocked = ref(props.locked);
		const isLockedClassname = computed(() => isLocked.value ? 'locked' : '');
		const ratio = ref(1);
		const isField1Changing = ref(false);
		const isField2Changing = ref(false);
		const unlockableClassname = computed(() => !props.unlockable ? 'disabled' : '');
		
    
    /**
     * check the ratio between field1 and field2
     * if the ratio is different from one, look at the biggest field result and
     * change both size if maxLimit reached
     * */
		watch(
			() => props.field1,
			(value, prevValue) => {
				// to ensure it wouldn't trigger infinite by each other
				// using anchor isField1Changing/isField2Changing alongside nexttick
				// to detect which field particularly has been changed
				if(props.ignoreWatch) getRatio();
				if (isLocked.value && !isField2Changing.value && !props.ignoreWatch) {
					isField1Changing.value = true;
          // if the max limit is set
          if(props.maxLimit){
            // field1 > field2
            if(ratio.value > 1){
							// if field1 > maxLimit
							if(value > props.maxLimit){
								// set field1 to maxLimit
								value = props.maxLimit;
								// set field2 to maxLimit / ratio
								emit("update:field2", Math.round(props.maxLimit / ratio.value));
							} else {
                value = value && value > 0 ? Math.round(value / ratio.value) : null;
                emit("update:field2", value);
							}
						}
            // field1 < field 2
            if(ratio.value < 1){
							// if result of field2 > maxLimit
							if(Math.round(value / ratio.value) > props.maxLimit){
								// set field2 to maxLimit
								emit("update:field2", props.maxLimit);
								// set field1 to maxLimit * ratio
								value = Math.round(props.maxLimit * ratio.value);
                isField2Changing.value = true; // mimic field2 changing to avoid infinite cycle
                emit("update:field1", value);
							} else {
                value = value && value > 0 ? Math.round(value / ratio.value) : null;
                emit("update:field2", value);
							}
            }
          }
          
					if(!props.maxLimit || ratio.value === 1){
            value = value && value > 0 ? Math.round(value / ratio.value) : null;
            emit("update:field2", value);
					}
     
					nextTick(() => {
            isField1Changing.value = false;
            isField2Changing.value = false;
          });
				}
			}
		)
		watch(
			() => props.field2,
			(value, prevValue) => {
        if(props.ignoreWatch) getRatio();
				if (isLocked.value && !isField1Changing.value && !props.ignoreWatch) {
					isField2Changing.value = true;
					// if the max limit is set
					if(props.maxLimit){
            // field 1 > field 2
						if(ratio.value > 1){
              // if result of field1 > maxLimit
							if(Math.round(value * ratio.value) > props.maxLimit){
								// set field1 to maxLimit
								emit("update:field1", props.maxLimit);
								// set field2 to maxLimit / ratio
								value = Math.round(props.maxLimit / ratio.value);
								isField1Changing.value = true; // mimic field1 changing to avoid infinite cycle
								emit("update:field2", value);
							} else {
								value = value && value > 0 ? Math.round(value * ratio.value) : null;
								emit("update:field1", value);
							}
						}
            // field 1 < field 2
						if(ratio.value < 1){
              // if field2 > maxLimit
							if(value > props.maxLimit){
								// set field2 to maxLimit
								value = props.maxLimit;
								// set field1 to maxLimit * ratio
								emit("update:field1", Math.round(props.maxLimit * ratio.value));
							} else {
								value = value && value > 0 ? Math.round(value * ratio.value) : null;
								emit("update:field1", value);
							}
						}
					}
     
					if(!props.maxLimit || ratio.value === 1){
            value = value && value > 0 ? Math.round(value * ratio.value) : null;
            emit("update:field1", value);
					}
     
					nextTick(() => {
            isField2Changing.value = false;
            isField1Changing.value = false;
          });
				}
			}
		)
		watch(
			() => props.unlockable,
			(value, prevValue) => {
				getRatio();
			}
		)
		
		function toggleLocked() {
			if (!props.unlockable) return;
			isLocked.value = !isLocked.value;
			getRatio();
		}
		
		function getRatio(){
			const computedRatio = parseFloat((props.field1 / props.field2).toFixed(8)) || 1;
			ratio.value =
				isLocked.value ?
				 computedRatio :
					1
		}
		
		onMounted(() => {
			getRatio();
		})
		
		return {
			isLockedClassname,
			unlockableClassname,
			toggleLocked,
			ratio
		}
	}
}
</script>

<style lang="scss">
@use "@/scss/sections/editor/elements/field/field-variables" as *;
@use "@/scss/sections/editor/elements/field/field-locked";
</style>
